import React, { useCallback, useContext, useEffect, useState } from "react";
import AuthContext from "../../context/userAuth.context";
import { loadWhatsNew, markWhatsNewRead } from "../../Utils/whatsNew";
import WhatsNewPostView from "./WhatsNewPostView";
import WhatsNewAdmin from "../../Administration/WhatsNewAdmin";

// "What's new" sidebar on the home screen: the latest Toons updates and news.
// The posts come from the server (Utils/whatsNew.js); admins write them in the Admin Panel,
// or with "Manage" at the top of this sidebar.
// A button in the header opens it; it also opens by itself once for each new post the user
// has not marked as read.

const BLUE = "#296AFF";
const AUTO_SHOWN_KEY = "toons_whats_new_auto_shown";

export function WhatsNewButton({ onClick, unread }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="What's new in Toons"
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 34,
        padding: "0 14px",
        border: "1px solid #dfe5f2",
        borderRadius: 17,
        background: "#fff",
        color: "#1d2433",
        fontSize: 14,
        fontFamily: "inherit",
        cursor: "pointer",
      }}
    >
      <span className="material-icons" style={{ fontSize: 18, color: BLUE }}>
        auto_awesome
      </span>
      What's new
      {unread && (
        <span
          aria-label="New updates"
          style={{
            position: "absolute",
            top: -2,
            right: -2,
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: "#ef4444",
            border: "2px solid #fff",
          }}
        />
      )}
    </button>
  );
}

export default function WhatsNew({ open, onClose, onRead, posts, loading, error, isAdmin, onManage }) {
  const [page, setPage] = useState(0);
  const list = posts || [];
  const total = list.length;
  const post = list[Math.min(page, Math.max(0, total - 1))];

  // Back to the newest post each time it opens; Escape closes it.
  useEffect(() => {
    if (open) setPage(0);
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const markRead = () => {
    if (onRead) onRead();
    onClose();
  };

  const pagerButton = (label, icon, to, disabled, iconFirst) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setPage(to)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        whiteSpace: "nowrap",
        border: "none",
        background: "none",
        padding: "4px 6px",
        fontSize: 13,
        fontFamily: "inherit",
        color: disabled ? "#b8bfcc" : "#1d2433",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {iconFirst && <span style={{ fontSize: 18, lineHeight: 1 }}>{icon}</span>}
      {label}
      {!iconFirst && <span style={{ fontSize: 18, lineHeight: 1 }}>{icon}</span>}
    </button>
  );

  const note = (text) => <div style={{ marginTop: 24, fontSize: 14, color: "#7a8396" }}>{text}</div>;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1300 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(20, 24, 35, 0.35)" }} />
      <aside
        role="dialog"
        aria-label="What's new"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(400px, 100vw)",
          background: "#fff",
          boxShadow: "-8px 0 30px rgba(0,0,0,.18)",
          display: "flex",
          flexDirection: "column",
          color: "#1d2433",
          animation: "toonsWhatsNewIn .22s ease-out",
        }}
      >
        <style>{"@keyframes toonsWhatsNewIn{from{transform:translateX(40px);opacity:0}to{transform:none;opacity:1}}"}</style>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "22px 24px 8px" }}>
          <div style={{ fontSize: 20, fontWeight: 600 }}>What's new</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {isAdmin && (
              <button
                type="button"
                onClick={onManage}
                title="Write and publish What's new posts"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  border: "1px solid #dfe5f2",
                  borderRadius: 14,
                  background: "#fff",
                  padding: "3px 10px",
                  fontSize: 12,
                  fontFamily: "inherit",
                  color: "#1d2433",
                  cursor: "pointer",
                }}
              >
                <span className="material-icons" style={{ fontSize: 15 }}>
                  edit
                </span>
                Manage
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{ border: "none", background: "none", cursor: "pointer", color: "#5b6476", lineHeight: 0, padding: 4 }}
            >
              <span style={{ fontSize: 26, lineHeight: "22px", display: "inline-block", width: 22 }}>{"×"}</span>
            </button>
          </div>
        </div>
        <div style={{ padding: "0 24px", fontSize: 13, color: "#7a8396" }}>
          {total === 0 ? "" : page === 0 ? "Latest updates to Toons" : "Earlier updates"}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 16px" }}>
          {loading && total === 0 && note("Loading…")}
          {!loading && error && total === 0 && note("What's new could not be loaded. Please try again later.")}
          {!loading && !error && total === 0 && note("No updates yet.")}
          <WhatsNewPostView post={post} />
        </div>

        <div style={{ borderTop: "1px solid #eef1f6", padding: "12px 24px 20px" }}>
          {total > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 12 }}>
              {pagerButton("Newer", "‹", page - 1, page === 0, true)}
              <span style={{ fontSize: 13, color: "#5b6476", whiteSpace: "nowrap" }}>
                Page {page + 1} of {total}
              </span>
              {pagerButton("Older", "›", page + 1, page >= total - 1, false)}
            </div>
          )}
          <button
            type="button"
            onClick={total ? markRead : onClose}
            style={{
              width: "100%",
              height: 40,
              border: "none",
              borderRadius: 8,
              background: BLUE,
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            {total ? "Mark as read" : "Close"}
          </button>
        </div>
      </aside>
    </div>
  );
}

/**
 * Loads the posts and keeps the panel's state. Opens the panel by itself once on the home screen
 * for each new post the user has not read. `panel` goes straight to <WhatsNew {...panel} />.
 */
export function useWhatsNew(isHome) {
  const { authenticationData, Role } = useContext(AuthContext);
  const apiBaseURL = authenticationData && authenticationData.ApiBaseURL;
  const isAdmin = String(Role || "").toLowerCase() === "admin";

  const [open, setOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [lastSeenPostId, setLastSeenPostId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const reload = useCallback(() => {
    if (!apiBaseURL) return;
    setLoading(true);
    loadWhatsNew(apiBaseURL)
      .then((data) => {
        setPosts(data.posts);
        setLastSeenPostId(data.lastSeenPostId);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [apiBaseURL]);

  useEffect(() => {
    reload();
  }, [reload]);

  const newest = posts[0];
  const unread = !!newest && newest.id !== lastSeenPostId;

  // Only once per post: after that, the dot on the button is the reminder.
  useEffect(() => {
    if (!isHome || !unread) return;
    const id = String(newest.id);
    let shown = null;
    try {
      shown = localStorage.getItem(AUTO_SHOWN_KEY);
      localStorage.setItem(AUTO_SHOWN_KEY, id);
    } catch (e) {
      /* no storage: open it */
    }
    // (Before the posts moved to the server this held the old code id, e.g. "2026-10-a".)
    if (shown !== id && !(newest.slug && shown === newest.slug)) setOpen(true);
  }, [isHome, unread, newest]);

  const read = () => {
    if (!newest) return;
    setLastSeenPostId(newest.id);
    markWhatsNewRead(apiBaseURL, newest.id).catch(() => {
      /* not saved: shows as new again next time */
    });
  };

  return {
    open,
    unread,
    show: () => setOpen(true),
    close: () => setOpen(false),
    read,
    reload,
    panel: {
      open,
      onClose: () => setOpen(false),
      onRead: read,
      posts,
      loading,
      error,
      isAdmin,
      onManage: () => setManageOpen(true),
    },
    admin: isAdmin && manageOpen && (
      <WhatsNewAdmin
        open
        onClose={() => {
          setManageOpen(false);
          reload();
        }}
      />
    ),
  };
}
